import { validateAccountInfo } from '@musinsa/apis-curator';
import { lazyPipe } from '@musinsa/packages-functional';
import {
  validateServiceRequest,
  validateServiceResponse,
  withServiceErrorCapture,
} from '@musinsa/services-common-service-zod4';

import { z } from 'zod';
import type {
  ValidateAccountServiceRequest,
  ValidateAccountServiceResponse,
} from './accountInfo.dto';

// 요청 검증 스키마
const ValidateAccountServiceRequestSchema = z.object({
  bankName: z.string().min(1, '은행명을 입력해주세요.'),
  accountNumber: z.string().min(1, '계좌번호를 입력해주세요.'),
  accountHolder: z.string().min(1, '예금주를 입력해주세요.'),
});

// 응답 검증 스키마
const ValidateAccountServiceResponseSchema = z.object({
  isValid: z.boolean(),
  failureReason: z.string().nullable(),
  errorMessage: z.string().optional(),
});

const transformApiResponseToServiceDto = async (
  request: ValidateAccountServiceRequest,
): Promise<ValidateAccountServiceResponse> => {
  try {
    const response = await validateAccountInfo(request);
    const apiData = response.data;

    // API 응답을 Services 레이어 DTO로 변환
    return {
      isValid: apiData.isValid,
      failureReason: apiData.failureReason,
      errorMessage: apiData.isValid ? undefined : getErrorMessage(apiData.failureReason),
    };
  } catch (_error) {
    // API 호출 실패 시 처리
    return {
      isValid: false,
      failureReason: 'API_ERROR',
      errorMessage: '계좌 인증 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
    };
  }
};

// 실패 사유에 따른 에러 메시지 생성
const getErrorMessage = (failureReason: string | null): string => {
  switch (failureReason) {
    case 'UNSUPPORTED_BANK':
      return '지원하지 않는 은행입니다.';
    case 'NAME_MISMATCH':
      return '예금주는 큐레이터 회원명과 일치해야 합니다.';
    case 'ACCOUNT_INVALID':
      return '예금주와 계좌번호 정보가 동일하지 않습니다.';
    default:
      return '계좌 인증에 실패했습니다. 입력 정보를 확인해주세요.';
  }
};

export const validateAccountService = withServiceErrorCapture(
  lazyPipe(
    validateServiceRequest(ValidateAccountServiceRequestSchema),
    transformApiResponseToServiceDto,
    validateServiceResponse(ValidateAccountServiceResponseSchema),
  ),
);
